import './SyncModal.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  ArrowsCounterClockwise,
  CheckCircle,
  Warning,
  XCircle,
  Info,
  Envelope,
  CalendarBlank,
  FileArrowUp,
  RedditLogo,
  HandWaving,
  Lightning,
  Trash,
  Spinner,
  SlackLogo,
} from '@phosphor-icons/react';
import { useContactsAppStore } from '../../store/contactsAppStore';
import { integrationSystem } from '../../../../services/integrations/core/IntegrationSystem';
import { IS_DEV } from '../../../../utils/devMode';
import { SyncLogEntry, SyncStatus } from '../../types/state';

interface SyncModalProps {
  windowId: string;
  spaceId: string;
  onClose: () => void;
  onImportClick: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

function StatusDot({ status }: { status: SyncStatus }) {
  return <span className={`sync-modal__log-dot sync-modal__log-dot--${status}`} />;
}

function StatusIcon({ status }: { status: SyncStatus }) {
  const size = 13;
  switch (status) {
    case 'success':   return <CheckCircle size={size} weight="fill" className="sync-modal__log-icon sync-modal__log-icon--success" />;
    case 'warning':   return <Warning size={size} weight="fill" className="sync-modal__log-icon sync-modal__log-icon--warning" />;
    case 'error':     return <XCircle size={size} weight="fill" className="sync-modal__log-icon sync-modal__log-icon--error" />;
    case 'duplicate': return <ArrowsCounterClockwise size={size} className="sync-modal__log-icon sync-modal__log-icon--duplicate" />;
    default:          return <Info size={size} className="sync-modal__log-icon sync-modal__log-icon--info" />;
  }
}

const OP_LABELS: Record<string, string> = {
  discover: 'discover',
  enrich: 'enrich',
  import: 'import',
  find_or_create: 'find/create',
  log_interaction: 'interaction',
  sources_check: 'sources',
};

function LogEntry({ entry }: { entry: SyncLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = !!entry.detail;

  return (
    <div
      className={`sync-modal__log-entry${hasDetail ? ' sync-modal__log-entry--expandable' : ''}`}
      onClick={hasDetail ? () => setExpanded((v) => !v) : undefined}
    >
      <div className="sync-modal__log-entry-main">
        <StatusIcon status={entry.status} />
        <span className="sync-modal__log-time">{formatTime(entry.at)}</span>
        <span className="sync-modal__log-op">{OP_LABELS[entry.operation] ?? entry.operation}</span>
        <span className="sync-modal__log-msg">{entry.message}</span>
        {hasDetail && (
          <span className="sync-modal__log-expand">{expanded ? '▲' : '▼'}</span>
        )}
      </div>
      {expanded && entry.detail && (
        <div className="sync-modal__log-detail">↳ {entry.detail}</div>
      )}
    </div>
  );
}

// ── Source tile ───────────────────────────────────────────────────────────

interface SourceTileProps {
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  stat?: string;
  note?: string;
  action?: { label: string; onClick: () => void; loading?: boolean };
}

function SourceTile({ name, icon, connected, stat, note, action }: SourceTileProps) {
  return (
    <div className={`sync-modal__tile${connected ? ' sync-modal__tile--connected' : ''}`}>
      <div className="sync-modal__tile-header">
        <span className={`sync-modal__tile-dot${connected ? ' sync-modal__tile-dot--on' : ''}`} />
        <span className="sync-modal__tile-icon">{icon}</span>
        <span className="sync-modal__tile-name">{name}</span>
      </div>
      {stat && <div className="sync-modal__tile-stat">{stat}</div>}
      {note && <div className="sync-modal__tile-note">{note}</div>}
      {action && (
        <button
          className="sync-modal__tile-btn"
          onClick={action.onClick}
          disabled={action.loading}
        >
          {action.loading ? <Spinner size={12} className="sync-modal__spin" /> : null}
          {action.label}
        </button>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────

export const SyncModal: React.FC<SyncModalProps> = ({ windowId, spaceId, onClose, onImportClick }) => {
  const store = useContactsAppStore(windowId, spaceId);

  const sourcesStatus   = store((s) => s.sourcesStatus);
  const isLoadingSources = store((s) => s.isLoadingSources);
  const syncLog         = store((s) => s.syncLog);
  const contacts        = store((s) => s.contacts);
  const isDiscovering   = store((s) => s.isDiscovering);
  const isImporting     = store((s) => s.isImporting);

  const discoverContacts  = store((s) => s.discoverContacts);
  const enrichContact     = store((s) => s.enrichContact);
  const clearSyncLog      = store((s) => s.clearSyncLog);
  const fetchSourcesStatus = store((s) => s.fetchSourcesStatus);

  const [isEnrichingAll, setIsEnrichingAll] = useState(false);
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);
  const [isConnectingSlack, setIsConnectingSlack] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  // Scroll log to top when new entries arrive
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = 0;
    }
  }, [syncLog.length]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDiscover = useCallback(async () => {
    await discoverContacts();
    // Refresh sources after discover so counts update
    fetchSourcesStatus();
  }, [discoverContacts, fetchSourcesStatus]);

  const handleEnrichAll = useCallback(async () => {
    if (!window.confirm(`Enrich all ${contacts.length} contacts? This will make up to ${contacts.length} LLM calls.`)) return;
    setIsEnrichingAll(true);
    for (const contact of contacts) {
      await enrichContact(contact._id);
      // Small delay to avoid hammering the backend
      await new Promise((r) => setTimeout(r, 200));
    }
    setIsEnrichingAll(false);
    fetchSourcesStatus();
  }, [contacts, enrichContact, fetchSourcesStatus]);

  const handleConnectGmail = useCallback(async () => {
    try {
      setIsConnectingGmail(true);
      const { connectComposioToolkit } = await import('../../../../services/composioService');
      await connectComposioToolkit('Gmail');
      fetchSourcesStatus();
    } catch (err: any) {
      if (!err.message?.includes('timeout') && !err.message?.includes('cancelled')) {
        console.error('[SyncModal] Failed to connect Gmail:', err);
      }
    } finally {
      setIsConnectingGmail(false);
    }
  }, [fetchSourcesStatus]);

  const handleConnectCalendar = useCallback(async () => {
    try {
      setIsConnectingCalendar(true);
      const integration = await integrationSystem.createIntegration('', 'GoogleCalendar');
      await integrationSystem.connectIntegration(integration._id);
      fetchSourcesStatus();
    } catch (err: any) {
      if (!err.message?.includes('cancelled')) {
        console.error('[SyncModal] Failed to connect Calendar:', err);
      }
    } finally {
      setIsConnectingCalendar(false);
    }
  }, [fetchSourcesStatus]);

  const handleConnectSlack = useCallback(async () => {
    try {
      setIsConnectingSlack(true);
      const integration = await integrationSystem.createIntegration('', 'Slack');
      await integrationSystem.connectIntegration(integration._id);
      fetchSourcesStatus();
    } catch (err: any) {
      if (!err.message?.includes('cancelled')) {
        console.error('[SyncModal] Failed to connect Slack:', err);
      }
    } finally {
      setIsConnectingSlack(false);
    }
  }, [fetchSourcesStatus]);

  const gmail = sourcesStatus?.gmail;
  const calendar = sourcesStatus?.calendar;
  const contactCounts = sourcesStatus?.contacts;
  const slack = sourcesStatus?.slack;
  const bySource = contactCounts?.by_source ?? {};

  return (
    <div className="sync-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sync-modal__panel">

        {/* Header */}
        <div className="sync-modal__header">
          <div className="sync-modal__header-left">
            <ArrowsCounterClockwise size={16} weight="bold" />
            <span className="sync-modal__title">Sync Center</span>
          </div>
          <button className="sync-modal__close" onClick={onClose} title="Close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="sync-modal__body">

          {/* Sources */}
          <section className="sync-modal__section">
            <div className="sync-modal__section-header">
              <h3 className="sync-modal__section-title">Data Sources</h3>
              {isLoadingSources && <Spinner size={12} className="sync-modal__spin" />}
            </div>

            <div className="sync-modal__tiles">
              <SourceTile
                name={
                  (gmail?.account_count ?? 0) > 1
                    ? `Gmail (${gmail!.account_count} accounts)`
                    : gmail?.connected_accounts?.[0]
                    ? `Gmail · ${gmail.connected_accounts[0]}`
                    : 'Gmail'
                }
                icon={<Envelope size={14} />}
                connected={!!gmail?.is_connected}
                stat={
                  gmail?.has_data
                    ? `${gmail.thread_count.toLocaleString()} threads`
                    : gmail?.is_connected
                    ? 'Connected — no threads synced yet'
                    : undefined
                }
                action={gmail?.is_connected ? {
                  label: isDiscovering ? 'Discovering…' : 'Run Discover',
                  onClick: handleDiscover,
                  loading: isDiscovering,
                } : {
                  label: isConnectingGmail ? 'Connecting…' : 'Connect Gmail',
                  onClick: handleConnectGmail,
                  loading: isConnectingGmail,
                }}
              />
              <SourceTile
                name={
                  calendar?.connected_account
                    ? `Calendar · ${calendar.connected_account}`
                    : 'Calendar'
                }
                icon={<CalendarBlank size={14} />}
                connected={!!calendar?.is_connected}
                stat={
                  calendar?.has_data
                    ? `${calendar.event_count.toLocaleString()} events`
                    : calendar?.is_connected
                    ? 'Connected — no events synced yet'
                    : undefined
                }
                action={!calendar?.is_connected ? {
                  label: isConnectingCalendar ? 'Connecting…' : 'Connect Calendar',
                  onClick: handleConnectCalendar,
                  loading: isConnectingCalendar,
                } : undefined}
              />
              <SourceTile
                name="LinkedIn CSV"
                icon={<FileArrowUp size={14} />}
                connected={!!(bySource['linkedin'] || bySource['csv'])}
                stat={(bySource['linkedin'] || bySource['csv'])
                  ? `${(bySource['linkedin'] ?? 0) + (bySource['csv'] ?? 0)} contacts imported`
                  : undefined}
                note="Export LinkedIn connections → import here"
                action={{
                  label: isImporting ? 'Importing…' : 'Import CSV',
                  onClick: onImportClick,
                  loading: isImporting,
                }}
              />
              <SourceTile
                name="Reddit"
                icon={<RedditLogo size={14} />}
                connected={!!(bySource['reddit'])}
                stat={bySource['reddit'] ? `${bySource['reddit']} contacts` : undefined}
                note="Syncs via contacts-reddit-scan agent skill"
              />
              <SourceTile
                name="Slack"
                icon={<SlackLogo size={14} />}
                connected={!!(slack?.has_data)}
                stat={slack?.has_data
                  ? `${slack.interaction_count} DM${slack.interaction_count !== 1 ? 's' : ''} logged${slack.workspaces.length > 0 ? ` · ${slack.workspaces.map(w => w.name).join(', ')}` : ''}`
                  : undefined}
                action={!slack?.has_data ? {
                  label: isConnectingSlack ? 'Connecting…' : 'Connect Slack',
                  onClick: handleConnectSlack,
                  loading: isConnectingSlack,
                } : undefined}
              />
              <SourceTile
                name="Manual"
                icon={<HandWaving size={14} />}
                connected={!!(bySource['manual'])}
                stat={bySource['manual'] ? `${bySource['manual']} contacts` : undefined}
                note="Log touchpoints from the contact detail view"
              />
            </div>
          </section>

          {/* Contact counts summary */}
          {contactCounts && contactCounts.total > 0 && (
            <section className="sync-modal__section">
              <h3 className="sync-modal__section-title">Contact Counts</h3>
              <div className="sync-modal__counts">
                <span className="sync-modal__count-chip sync-modal__count-chip--total">
                  Total: {contactCounts.total}
                  {contactCounts.enriched_count > 0 && ` · Enriched: ${contactCounts.enriched_count}`}
                </span>
                {Object.entries(bySource).map(([src, n]) => (
                  <span key={src} className="sync-modal__count-chip">
                    {src}: {n}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Operations */}
          <section className="sync-modal__section">
            <h3 className="sync-modal__section-title">Operations</h3>
            <div className="sync-modal__ops">
              <button
                className={`sync-modal__op-btn sync-modal__op-btn--primary${isDiscovering ? ' sync-modal__op-btn--loading' : ''}`}
                onClick={handleDiscover}
                disabled={isDiscovering}
              >
                <ArrowsCounterClockwise size={14} weight="bold" />
                {isDiscovering ? 'Discovering…' : 'Discover All'}
              </button>

              {IS_DEV && (
                <button
                  className={`sync-modal__op-btn sync-modal__op-btn--dev${isEnrichingAll ? ' sync-modal__op-btn--loading' : ''}`}
                  onClick={handleEnrichAll}
                  disabled={isEnrichingAll || contacts.length === 0}
                  title={`Enrich all ${contacts.length} contacts (LLM calls, dev only)`}
                >
                  <Lightning size={14} weight="bold" />
                  {isEnrichingAll ? `Enriching… (${contacts.length})` : `Enrich All (${contacts.length})`}
                </button>
              )}

              <button
                className="sync-modal__op-btn"
                onClick={() => clearSyncLog()}
                disabled={syncLog.length === 0}
              >
                <Trash size={14} />
                Clear Log
              </button>
            </div>
          </section>

          {/* Activity log */}
          <section className="sync-modal__section sync-modal__section--log">
            <div className="sync-modal__section-header">
              <h3 className="sync-modal__section-title">Activity Log</h3>
              <span className="sync-modal__log-count">{syncLog.length} entries</span>
            </div>
            <div className="sync-modal__log" ref={logRef}>
              {syncLog.length === 0 ? (
                <div className="sync-modal__log-empty">
                  No activity yet — run Discover or Enrich to populate your contacts.
                </div>
              ) : (
                syncLog.map((entry) => (
                  <LogEntry key={entry.id} entry={entry} />
                ))
              )}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};
